import React, { useEffect } from "react";
import Navbar from "./Navbar";
import Footer from "./Footer";
import SeoHead from "./SeoHead";

const PageLayout = ({ children, seoProps = {} }) => {
  useEffect(() => {
    // Optimizar carga de página
    const optimizePageLoading = () => {
      // Desactivar animaciones mientras se carga la página para mejorar rendimiento
      document.documentElement.classList.add("loading-optimization");

      // Registrar tiempo de carga
      const startTime = performance.now();

      // Ejecutar cuando la página esté completamente cargada
      window.addEventListener("load", () => {
        // Calcular tiempo de carga
        const loadTime = performance.now() - startTime;
        console.log(`Página cargada en ${loadTime.toFixed(2)}ms`);

        // Permitir animaciones de nuevo
        setTimeout(() => {
          document.documentElement.classList.remove("loading-optimization");
        }, 100);

        // Precargar enlaces que son visibles en la pantalla
        const prefetchVisibleLinks = () => {
          const links = document.querySelectorAll("a");

          links.forEach((link) => {
            if (
              isElementInViewport(link) &&
              link.href &&
              link.href.startsWith(window.location.origin)
            ) {
              const prefetchLink = document.createElement("link");
              prefetchLink.rel = "prefetch";
              prefetchLink.href = link.href;
              document.head.appendChild(prefetchLink);
            }
          });
        };

        // Verificar si un elemento es visible
        const isElementInViewport = (el) => {
          const rect = el.getBoundingClientRect();
          return (
            rect.top >= 0 &&
            rect.left >= 0 &&
            rect.bottom <=
              (window.innerHeight || document.documentElement.clientHeight) &&
            rect.right <=
              (window.innerWidth || document.documentElement.clientWidth)
          );
        };

        // Precargar enlaces visibles
        prefetchVisibleLinks();
      });
    };

    // Implementar optimizaciones de rendimiento
    optimizePageLoading();

    // Configurar encabezados de seguridad (CSP) en tiempo de ejecución
    const setSecurityHeaders = () => {
      // Estas son emulaciones client-side; los encabezados reales deben configurarse en el servidor
      console.info(
        "Seguridad: Políticas de seguridad activadas para protección de contenido"
      );
    };

    setSecurityHeaders();

    // Script para detectar y bloquear herramientas de captura
    const setupCaptureProtection = () => {
      // Detectar extensiones de captura de pantalla (no es 100% efectivo)
      setTimeout(() => {
        const testElement = document.createElement("div");
        testElement.id = "captureDetectionTest";
        testElement.style.position = "fixed";
        testElement.style.opacity = "0.01";
        testElement.style.visibility = "hidden";
        testElement.textContent = "capture-test";
        document.body.appendChild(testElement);

        const captureDetector = setInterval(() => {
          const element = document.getElementById("captureDetectionTest");
          if (element) {
            const computed = window.getComputedStyle(element);
            const visibility = computed.getPropertyValue("visibility");

            // Algunas herramientas de captura alteran la visibilidad o el estilo
            if (visibility !== "hidden" || computed.opacity !== "0.01") {
              console.warn("Posible intento de captura detectado");
              // Aquí podrías implementar medidas de protección adicionales
            }
          } else {
            clearInterval(captureDetector);
          }
        }, 1000);

        // Limpiar después de un tiempo
        setTimeout(() => {
          clearInterval(captureDetector);
          if (testElement.parentNode) {
            testElement.parentNode.removeChild(testElement);
          }
        }, 10000);
      }, 2000);
    };

    setupCaptureProtection();

    // Marcar todas las imágenes como protegidas para SEO y legalmente
    const markImagesAsProtected = () => {
      document.querySelectorAll("img").forEach((img) => {
        if (!img.alt) img.alt = "Obra de arte protegida por derechos de autor";
        if (!img.getAttribute("data-protected")) {
          img.setAttribute("data-protected", "true");
          img.setAttribute("data-owner", "ARTE OLIVIERI");
          img.setAttribute("data-license", "All Rights Reserved");
        }
      });
    };

    window.addEventListener("load", markImagesAsProtected);

    return () => {
      window.removeEventListener("load", markImagesAsProtected);
    };
  }, []);

  // Estructura datos JSON-LD para SEO
  const generateStructuredData = () => {
    return {
      "@context": "https://schema.org",
      "@type": "ItemList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          item: {
            "@type": "VisualArtwork",
            name: "Resonancia Onírica",
            creator: {
              "@type": "Person",
              name: "ARTE OLIVIERI",
            },
            artform: "Digital Art",
            artMedium: "Digital Print on Cotton Paper",
            artworkSurface: "Cotton Paper",
            description:
              "Una exploración de la interfaz entre los sueños y la realidad consciente",
            image:
              "https://images.unsplash.com/photo-1617791160505-6f00504e3519",
          },
        },
        {
          "@type": "ListItem",
          position: 2,
          item: {
            "@type": "VisualArtwork",
            name: "Cosmos Digital I",
            creator: {
              "@type": "Person",
              name: "ARTE OLIVIERI",
            },
            artform: "Digital Art",
            artMedium: "Digital Print on Cotton Paper",
            artworkSurface: "Cotton Paper",
            description:
              "Exploración visual de elementos cósmicos y espaciales",
            image:
              "https://images.unsplash.com/photo-1618005198919-d3d4b5a92ead",
          },
        },
      ],
    };
  };

  // SEO Props default con datos estructurados
  const defaultSeoProps = {
    structuredData: generateStructuredData(),
  };

  // Mezclar props por defecto con props proporcionados
  const finalSeoProps = { ...defaultSeoProps, ...seoProps };

  return (
    <div className="min-h-screen bg-primary-dark">
      {/* Componente SEO con metadatos optimizados */}
      <SeoHead {...finalSeoProps} />

      <Navbar />

      <main className="overflow-hidden">{children}</main>

      <Footer />

      {/* Estilos adicionales para optimización de carga */}
      <style>{`
        .loading-optimization * {
          animation-duration: 0.001s !important;
          animation-delay: 0s !important;
          transition-duration: 0.001s !important;
        }

        /* Estilos para mejoras de accesibilidad */
        :focus {
          outline: 2px solid #c7b299 !important;
          outline-offset: 2px !important;
        }

        @media (prefers-reduced-motion: reduce) {
          * {
            animation-duration: 0.001s !important;
            transition-duration: 0.001s !important;
            animation-iteration-count: 1 !important;
          }
        }

        /* Mejoras de rendimiento para imágenes */
        img {
          content-visibility: auto;
        }
  `}</style>

      {/* Fragmento para protección legal y copyright */}
      <div className="sr-only" aria-hidden="true">
        <h2>Información Legal sobre Derechos de Autor</h2>
        <p>
          Todas las obras, imágenes y contenido visual presentados en ARTE
          OLIVIERI están protegidos por leyes de derechos de autor. Queda
          prohibida cualquier forma de reproducción, distribución, o
          comunicación pública sin autorización expresa del titular de los
          derechos.
        </p>
        <p>
          © {new Date().getFullYear()} ARTE OLIVIERI. Todos los derechos
          reservados.
        </p>
      </div>
    </div>
  );
};

export default PageLayout;
